import { localeFor } from "./languages";
import { ui } from "./localization";
import { useCallback, useEffect, useRef, useState } from "react";
import { translations, getLocalizedError, type Language } from "../i18n";
import type { BrowserScreenshot, ChatMessage, Conversation, GeneratedFile, GeneratedImage, WebResearchState, WebSource } from "../types";
import { consumeChatStream } from "./stream";
import { createStreamBatcher } from "./stream-batcher";
import { isRecoverableStreamError, waitForPageVisible } from "./stream-recovery";
import { waitForImageJob } from "./image-jobs";
import { getClientId } from "./client-id";
import { getImageReferencesForRequest, stripInternalImageContext } from "./chat-context";
import { notifyGenerationComplete } from "./pwa";
import { ChatRequestError, fetchWithDeadline, readResponseJson } from "./request-errors";

const now = () => new Date().toISOString();
interface Options {
  language: Language;
  updateMessage: (conversationId: string, messageId: string, update: (message: ChatMessage) => ChatMessage) => void;
  prepareConversationContext: (conversation: Conversation, messages: ChatMessage[]) => { messages: unknown[] };
}
export function useChatGeneration({ language, updateMessage: commitMessage, prepareConversationContext }: Options) {
  const t = translations[language];
  const updateMessage = commitMessage;
  const detachedControllers = useRef(new WeakSet<AbortController>());
  const tasks = useRef(new Map<string, { sessionId: string; controller: AbortController }>());
  const [runningConversationIds, setRunningConversationIds] = useState<string[]>([]);
  const refreshTasks = () => setRunningConversationIds([...tasks.current.keys()]);
  useEffect(() => {
    const currentTasks = tasks.current;
    return () => { for (const task of currentTasks.values()) { detachedControllers.current.add(task.controller); task.controller.abort(); } currentTasks.clear(); };
  }, []);
  const generateResponse = useCallback(
    async (
      conversation: Conversation,
      contextMessages: ChatMessage[],
      assistantId: string,
      resumedMessage?: ChatMessage,
    ) => {
      const controller = new AbortController();
      const updateMessage = (conversationId: string, messageId: string, update: (message: ChatMessage) => ChatMessage) => {
        if (!detachedControllers.current.has(controller)) commitMessage(conversationId, messageId, update);
      };
      if (tasks.current.has(conversation.id)) return;
      tasks.current.set(conversation.id, { controller, sessionId: resumedMessage?.generationSessionId ?? assistantId });
      refreshTasks();
      const sessionId = resumedMessage?.generationSessionId ?? assistantId;
      const startedAt = Date.parse(resumedMessage?.createdAt ?? now());
      let content = resumedMessage?.content ?? "";
      let reasoning = resumedMessage?.reasoning ?? "";
      let generatedImages: GeneratedImage[] = resumedMessage?.generatedImages ?? [];
      let browserScreenshots: BrowserScreenshot[] = resumedMessage?.browserScreenshots ?? [];
      let generatedFiles: GeneratedFile[] = resumedMessage?.generatedFiles ?? [];
      let webResearch: WebResearchState | undefined = resumedMessage?.webResearch;
      let webSources: WebSource[] = resumedMessage?.webSources ?? [];
      let pendingImageJobId = resumedMessage?.imageGeneration?.status === "generating"
        ? resumedMessage.imageGeneration.jobId
        : undefined;
      let imageGeneration = resumedMessage?.imageGeneration;
      let streamCursor = resumedMessage?.streamCursor ?? 0;
      let cancelledByServer = false;
      const streamBatcher = createStreamBatcher();

      const recoverPendingImage = async (): Promise<boolean> => {
        if (!pendingImageJobId) return false;
        const image = await waitForImageJob({
          jobId: pendingImageJobId,
          clientId: getClientId(),
          signal: controller.signal,
          language,
        });
        pendingImageJobId = undefined;
        imageGeneration = { status: "complete", modelId: image.modelId, modelName: image.modelName, prompt: image.prompt };
        if (!generatedImages.some((item) => item.id === image.id)) generatedImages = [...generatedImages, image];
        updateMessage(conversation.id, assistantId, (message) => ({
          ...message,
          generatedImages,
          imageGeneration: {
            status: "complete",
            modelId: image.modelId,
            modelName: image.modelName,
            prompt: image.prompt,
          },
          status: "streaming",
        }));
        void notifyGenerationComplete(language, "image");
        return true;
      };

      try {
      const requestBody = resumedMessage ? undefined : JSON.stringify({
        model: conversation.modelId,
        messages: prepareConversationContext(conversation, contextMessages).messages,
        temperature: conversation.temperature,
        maxTokens: conversation.maxTokens,
        imageModel: conversation.imageModelId,
        researchMode: conversation.researchMode === true,
        imageReferences: getImageReferencesForRequest(contextMessages),
        timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC",
      });

        let recoveryAttempts = 0;
        let starting = !resumedMessage;
        while (true) {
          try {
            const endpoint = starting
              ? `/api/chat/sessions/${encodeURIComponent(sessionId)}`
              : `/api/chat/sessions/${encodeURIComponent(sessionId)}/events?cursor=${streamCursor}`;
            const response = await fetchWithDeadline(endpoint, {
              method: starting ? "POST" : "GET",
              headers: {
                ...(starting ? { "content-type": "application/json" } : {}),
                "x-neurondeck-client": getClientId(),
                "accept-language": localeFor(language),
              },
              ...(starting ? { body: requestBody } : {}),
              signal: controller.signal,
            });

            if (!response.ok) {
              const data = (await readResponseJson(response, controller.signal)) as
                | { error?: { code?: string; message?: string; retryAfter?: number } }
                | null;
              throw new ChatRequestError(
                getLocalizedError(language, data?.error?.code, data?.error?.message || t.requestFailed(response.status)),
                data?.error?.code, data?.error?.retryAfter ?? (Number(response.headers.get("retry-after")) || undefined),
              );
            }
            starting = false;
            updateMessage(conversation.id, assistantId, (message) => ({
              ...message,
              generationSessionId: sessionId,
              streamCursor,
              recoveryState: recoveryAttempts ? "recovering" : undefined,
            }));

            await consumeChatStream(response, (event) => {
              if (event.cursor != null) streamCursor = Math.max(streamCursor, event.cursor);
              if (event.error) {
                throw new ChatRequestError(getLocalizedError(language, event.errorCode, event.error), event.errorCode, event.retryAfter);
              }
              if (event.content) content = stripInternalImageContext(content + event.content);
              if (event.reasoning) reasoning += event.reasoning;
              if (event.cancelled) cancelledByServer = true;
              if (event.imageGeneration?.status === "generating" && event.imageGeneration.jobId) {
                pendingImageJobId = event.imageGeneration.jobId;
              }
              if (event.imageGeneration?.status === "error") pendingImageJobId = undefined;
              if (event.imageGeneration) imageGeneration = event.imageGeneration;
              if (event.generatedImage) {
                pendingImageJobId = undefined;
                imageGeneration = { status: "complete", modelId: event.generatedImage.modelId, modelName: event.generatedImage.modelName, prompt: event.generatedImage.prompt };
                if (!generatedImages.some((image) => image.id === event.generatedImage!.id)) {
                  generatedImages = [...generatedImages, event.generatedImage];
                }
              }
              if (event.webResearch) {
                webResearch = event.webResearch;
                const source = event.webResearch.source;
                if (source && !webSources.some((item) => item.url === source.url)) webSources = [...webSources, source];
              }
              if (event.browserScreenshot && !browserScreenshots.some((item) => item.id === event.browserScreenshot!.id)) {
                browserScreenshots = [...browserScreenshots, event.browserScreenshot];
              }
              if (event.generatedFile && !generatedFiles.some((item) => item.id === event.generatedFile!.id)) {
                generatedFiles = [...generatedFiles, event.generatedFile];
              }
              if (event.content || event.reasoning || event.generatedImage || event.imageGeneration || event.webResearch || event.browserScreenshot || event.generatedFile || event.cursor != null) {
                streamBatcher.schedule(() => updateMessage(conversation.id, assistantId, (message) => ({
                  ...message,
                  error: undefined, errorCode: undefined, retryAt: undefined,
                  content,
                  reasoning,
                  generatedImages,
                  browserScreenshots,
                  generatedFiles,
                  webResearch,
                  webSources,
                  imageGeneration,
                  status: "streaming",
                  generationSessionId: sessionId,
                  streamCursor,
                  recoveryState: undefined,
                })));
              }
            }, { signal: controller.signal });
            streamBatcher.flush();
            break;
          } catch (error) {
            streamBatcher.flush();
            if (controller.signal.aborted) throw new DOMException("Aborted", "AbortError");
            if (!isRecoverableStreamError(error) || recoveryAttempts >= 8) throw error;

            recoveryAttempts += 1;
            updateMessage(conversation.id, assistantId, (message) => ({
              ...message,
              generationSessionId: sessionId,
              streamCursor,
              recoveryState: "recovering",
            }));
            await waitForPageVisible(controller.signal);
            await new Promise<void>((resolve, reject) => {
              const handleAbort = () => {
                window.clearTimeout(timeout);
                reject(new DOMException("Aborted", "AbortError"));
              };
              const timeout = window.setTimeout(() => {
                controller.signal.removeEventListener("abort", handleAbort);
                resolve();
              }, Math.min(3_000, 300 * recoveryAttempts));
              controller.signal.addEventListener("abort", handleAbort, { once: true });
            });
          }
        }

        if (detachedControllers.current.has(controller)) return;
        if (webResearch && (webResearch.status === "searching" || webResearch.status === "reading" || webResearch.status === "capturing")) {
          webResearch = { ...webResearch, status: "complete" };
        }

        updateMessage(conversation.id, assistantId, (message) => ({
          ...message,
          content: cancelledByServer
            ? content || t.generationStopped
            : content || (generatedImages.length
              ? t.imageGeneratedFallback
              : browserScreenshots.length
                ? t.screenshotCapturedFallback
                : generatedFiles.length
                  ? t.fileGeneratedFallback
                  : t.emptyCompletion),
          reasoning,
          generatedImages,
          browserScreenshots,
          generatedFiles,
          webResearch,
          webSources,
          imageGeneration: imageGeneration?.status === "generating" && cancelledByServer ? { ...imageGeneration, status: "error", message: t.generationStopped } : imageGeneration,
          status: "complete",
          elapsedMs: Math.max(1, Date.now() - startedAt),
          generationSessionId: sessionId,
          streamCursor,
          recoveryState: undefined,
        }));
        if (!cancelledByServer) {
          void notifyGenerationComplete(language, generatedImages.length ? "image" : "chat");
        }
      } catch (error) {
        if (detachedControllers.current.has(controller)) return;
        streamBatcher.flush();
        const stopped = error instanceof DOMException && error.name === "AbortError";
        if (!stopped && pendingImageJobId) {
          try {
            await recoverPendingImage();
          } catch {
            // Preserve the stream error below if the separate image job is also unavailable.
          }
        }
        const message = stopped
          ? content || t.generationStopped
          : error instanceof Error
            ? error.message
            : t.generationFailed;
        updateMessage(conversation.id, assistantId, (current) => ({
          ...current,
          content: content || (stopped ? t.generationStopped : ""),
          error: stopped ? undefined : message,
          errorCode: stopped ? undefined : error instanceof ChatRequestError ? error.code : isRecoverableStreamError(error) ? "network_error" : undefined,
          retryAt: error instanceof ChatRequestError && error.retryAfter ? new Date(Date.now() + error.retryAfter * 1000).toISOString() : undefined,
          reasoning,
          generatedImages,
          browserScreenshots,
          generatedFiles,
          webSources,
          imageGeneration: imageGeneration?.status === "generating" ? { ...imageGeneration, status: "error", message: stopped ? t.generationStopped : message } : imageGeneration,
          webResearch: webResearch && webResearch.status !== "complete" ? { ...webResearch, status: "error", message: stopped ? t.generationStopped : message } : webResearch,
          status: stopped ? "complete" : "error",
          elapsedMs: Math.max(1, Date.now() - startedAt),
          generationSessionId: sessionId,
          streamCursor,
          recoveryState: undefined,
        }));
      } finally {
        streamBatcher.flush();
        if (tasks.current.get(conversation.id)?.controller === controller) tasks.current.delete(conversation.id);
        refreshTasks();
      }
    },
    [
      language,
      t.emptyCompletion,
      t.generationFailed,
      t.generationStopped,
      t.imageGeneratedFallback,
      t.fileGeneratedFallback,
      t.requestFailed,
      t.screenshotCapturedFallback,
      updateMessage,
    ],
  );

  const stopGeneration = useCallback(async (conversation: Conversation) => {
    const task = tasks.current.get(conversation.id);
    const pending = [...conversation.messages].reverse().find((message) => (message.status === "streaming" || message.errorCode === "image_cancel_pending" || message.errorCode === "cancel_failed") && message.generationSessionId);
    const sessionId = task?.sessionId ?? pending?.generationSessionId;
    if (!sessionId) return;
    // Only stop this conversation. Wait for server acknowledgment before detaching the reader.
    try {
      const response = await fetchWithDeadline(`/api/chat/sessions/${encodeURIComponent(sessionId)}/cancel`, {
        method: "POST", headers: { "x-neurondeck-client": getClientId() }, keepalive: true,
      });
      if (!response.ok) {
        const data = await readResponseJson(response) as { error?: { code?: string; message?: string; retryAfter?: number } } | null;
        throw new ChatRequestError(getLocalizedError(language, data?.error?.code, data?.error?.message ?? t.requestFailed(response.status)), data?.error?.code ?? "cancel_failed", data?.error?.retryAfter);
      }
      task?.controller.abort();
      if (pending) updateMessage(conversation.id, pending.id, (message) => ({ ...message, error: undefined, errorCode: undefined, retryAt: undefined }));
      if (!task && pending) updateMessage(conversation.id, pending.id, (message) => ({ ...message, status: "complete", recoveryState: undefined }));
    } catch (error) {
      if (pending) updateMessage(conversation.id, pending.id, (message) => ({ ...message,
        error: error instanceof ChatRequestError ? error.message : ui(language, "Stop was not confirmed. Please try again."),
        errorCode: error instanceof ChatRequestError ? error.code : "cancel_failed",
        retryAt: error instanceof ChatRequestError && error.retryAfter ? new Date(Date.now() + error.retryAfter * 1000).toISOString() : undefined,
      }));
    }
  }, [language, updateMessage, t.requestFailed]);
  const hasTask = useCallback((conversationId: string) => tasks.current.has(conversationId), []);
  return { generateResponse, stopGeneration, runningConversationIds, hasTask };
}
